import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Building, FileText, Package, Paperclip } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import { useClientes } from '../../clientes/hooks';
import { useUsuarios } from '../../usuarios/hooks';
import { CommercialBaseSelect } from '../../base-comercial/components/CommercialBaseSelect';
import { useCriarProposta } from '../hooks';

type WizardStep = 'cliente' | 'base' | 'itens' | 'anexos';

const STEPS: Array<{ id: WizardStep; label: string; icon: React.ReactNode }> = [
  { id: 'cliente', label: 'Cliente', icon: <Building className="h-4 w-4" /> },
  { id: 'base', label: 'Base Comercial', icon: <FileText className="h-4 w-4" /> },
  { id: 'itens', label: 'Itens', icon: <Package className="h-4 w-4" /> },
  { id: 'anexos', label: 'Anexos', icon: <Paperclip className="h-4 w-4" /> },
];

/**
 * Wizard de cadastro de proposta (US-COM-006, task 15.2).
 *
 * **Duas etapas de coleta essencial** — cliente + Base Comercial — e a
 * proposta é criada em versão 1.0 pelo backend. Itens (aba 3) e anexos
 * (aba 4) são carregados na `PropostaDetalhePage` depois que já
 * existem `proposalId` + `versionId`; a UI de edição vive lá para
 * evitar duplicação. As abas 3 e 4 aparecem no stepper como "próximos
 * passos" para o usuário saber o que vem.
 *
 * Sem `react-hook-form` (o projeto não usa) — formulário controlado com
 * `useState`, validação inline no `goNext`.
 */
export default function NovaPropostaPage(): React.ReactElement {
  const navigate = useNavigate();

  const [step, setStep] = useState<WizardStep>('cliente');
  const [clientId, setClientId] = useState('');
  const [commercialBaseId, setCommercialBaseId] = useState('');
  const [responsavelId, setResponsavelId] = useState('');
  const [dataValidade, setDataValidade] = useState(defaultValidade());
  const [escopo, setEscopo] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const criar = useCriarProposta();

  const { data: clientesData } = useClientes({ active: true, limit: 200 });
  const { data: usuariosData } = useUsuarios({ limit: 200 });

  const clientOptions = (clientesData?.data ?? []).map((c) => ({
    value: c.id,
    label: `${c.razaoSocial} (${c.code})`,
    description: c.documento,
  }));
  const usuarioOptions = (usuariosData?.data ?? []).map((u) => ({
    value: u.id,
    label: u.name || u.email,
    description: u.email,
  }));

  function goNext() {
    setErro(null);
    if (step === 'cliente') {
      if (!clientId) return setErro('Selecione um cliente para prosseguir.');
      if (!responsavelId) return setErro('Selecione o responsável comercial.');
      if (!dataValidade) return setErro('Informe a data de validade da proposta.');
      setStep('base');
      return;
    }
    if (step === 'base') {
      if (!commercialBaseId) return setErro('Selecione a Base Comercial vigente.');
      void handleSubmit();
      return;
    }
  }

  function goBack() {
    setErro(null);
    if (step === 'base') setStep('cliente');
    else navigate('/propostas');
  }

  async function handleSubmit() {
    setErro(null);
    try {
      const proposta = await criar.mutateAsync({
        clientId,
        commercialBaseId,
        responsavelComercialUserId: responsavelId,
        dataValidade,
        escopo: escopo.trim() || undefined,
        observacoes: observacoes.trim() || undefined,
      });
      navigate(`/propostas/${proposta.id}`);
    } catch (e) {
      setErro(extractError(e));
    }
  }

  const stepIndex = STEPS.findIndex((s) => s.id === step);

  return (
    <div className="p-4 sm:p-6">
      <button
        type="button"
        onClick={() => navigate('/propostas')}
        className="mb-4 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar
      </button>

      <h1 className="mb-2 text-xl font-semibold text-gray-900">Nova Proposta</h1>
      <p className="mb-6 text-sm text-gray-600">
        Cadastro em duas etapas — depois de criar, você pode adicionar itens e anexos na tela
        de detalhe.
      </p>

      <nav aria-label="Progresso" className="mb-6 flex gap-2 overflow-x-auto">
        {STEPS.map((s, i) => (
          <div
            key={s.id}
            className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${
              i === stepIndex
                ? 'border-blue-600 bg-blue-50 text-blue-700'
                : i < stepIndex
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                  : 'border-gray-200 text-gray-500'
            }`}
          >
            {s.icon}
            {s.label}
          </div>
        ))}
      </nav>

      {erro && (
        <Alert variant="error" className="mb-4">
          {erro}
        </Alert>
      )}

      {step === 'cliente' && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <AutocompleteSelect
            label="Cliente *"
            options={clientOptions}
            value={clientId}
            onChange={setClientId}
          />
          <AutocompleteSelect
            label="Responsável comercial *"
            options={usuarioOptions}
            value={responsavelId}
            onChange={setResponsavelId}
          />
          <TextInput
            label="Data de validade *"
            type="date"
            value={dataValidade}
            onChange={(e) => setDataValidade(e.target.value)}
          />
          <div className="md:col-span-2">
            <Textarea
              label="Escopo (opcional)"
              value={escopo}
              onChange={(e) => setEscopo(e.target.value)}
              rows={3}
              maxLength={4000}
            />
          </div>
          <div className="md:col-span-2">
            <Textarea
              label="Observações (opcional)"
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={2}
              maxLength={4000}
            />
          </div>
        </div>
      )}

      {step === 'base' && (
        <div className="space-y-4">
          <CommercialBaseSelect
            clientId={clientId}
            value={commercialBaseId}
            onChange={setCommercialBaseId}
            allowNone={false}
          />
          <p className="text-xs text-gray-500">
            A Base Comercial selecionada é congelada como snapshot na versão 1.0 (RN-COM-004).
            Substituir a Base depois não muda os valores desta proposta.
          </p>
        </div>
      )}

      <div className="mt-6 flex justify-between">
        <Button variant="secondary" onClick={goBack} disabled={criar.isPending}>
          Voltar
        </Button>
        <Button onClick={goNext} disabled={criar.isPending}>
          {step === 'base' ? (criar.isPending ? 'Criando…' : 'Criar proposta') : 'Próximo'}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function defaultValidade(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const resp = (e as { response?: { data?: { message?: string | string[] } } }).response;
    const msg = resp?.data?.message;
    if (Array.isArray(msg)) return msg.join('; ');
    if (typeof msg === 'string') return msg;
  }
  return e instanceof Error ? e.message : 'Erro desconhecido.';
}

export { NovaPropostaPage };
